import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe, Volume2, Copy, ArrowRightLeft, Mic, Loader2, Check, Languages, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸', speechCode: 'en-US' },
  { code: 'es', name: 'Español', flag: '🇪🇸', speechCode: 'es-ES' },
  { code: 'zh', name: '中文', flag: '🇨🇳', speechCode: 'zh-CN' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', speechCode: 'vi-VN' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', speechCode: 'ko-KR' },
  { code: 'tl', name: 'Tagalog', flag: '🇵🇭', speechCode: 'fil-PH' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', speechCode: 'hi-IN' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', speechCode: 'ar-SA' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', speechCode: 'ru-RU' },
  { code: 'pt', name: 'Português', flag: '🇧🇷', speechCode: 'pt-BR' },
];

// Medical phrases commonly needed in hospice care
const MEDICAL_PHRASES = [
  { en: "How is your pain level?", category: "assessment" },
  { en: "Take this medication with food", category: "medication" },
  { en: "Call us if symptoms worsen", category: "instructions" },
  { en: "Your next appointment is scheduled", category: "scheduling" },
  { en: "Do you need help with anything?", category: "care" },
  { en: "Let me know if you feel nauseous", category: "symptoms" },
  { en: "Try to rest and stay comfortable", category: "comfort" },
  { en: "Your loved one is peaceful", category: "family" },
  { en: "We are here to support you", category: "emotional" },
  { en: "Would you like to speak with a chaplain?", category: "spiritual" },
];

export default function PremiumTranslator({ trigger }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sourceText, setSourceText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [sourceLang, setSourceLang] = useState("en");
  const [targetLang, setTargetLang] = useState("es");
  const [isTranslating, setIsTranslating] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("text");

  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      toast.error("Please enter text to translate");
      return;
    }
    
    setIsTranslating(true);
    try {
      // Use AI for translation
      const response = await api.post("/translate", {
        text: sourceText,
        source_language: sourceLang,
        target_language: targetLang
      });
      
      setTranslatedText(response.data.translated_text);
    } catch (error) {
      // Fallback: try using browser's translation if AI fails
      toast.error("Translation service unavailable. Please try again.");
    } finally {
      setIsTranslating(false);
    }
  };

  const swapLanguages = () => {
    const tempLang = sourceLang;
    setSourceLang(targetLang);
    setTargetLang(tempLang);
    
    const tempText = sourceText;
    setSourceText(translatedText);
    setTranslatedText(tempText);
  };

  const speakText = (text, langCode) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(true);
      
      const lang = LANGUAGES.find(l => l.code === langCode);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang?.speechCode || 'en-US';
      utterance.rate = 0.9;
      
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const startVoiceInput = () => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      const lang = LANGUAGES.find(l => l.code === sourceLang);
      recognition.lang = lang?.speechCode || 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;
      
      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => setIsListening(false);
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSourceText(transcript);
      };
      
      recognition.start();
    } else {
      toast.error("Voice input not supported in this browser");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMedicalPhrase = async (phrase) => {
    setSourceText(phrase.en);
    setSourceLang("en");
    
    // Auto-translate
    setIsTranslating(true);
    try {
      const response = await api.post("/translate", {
        text: phrase.en,
        source_language: "en",
        target_language: targetLang
      });
      setTranslatedText(response.data.translated_text);
    } catch (e) {
      toast.error("Translation failed");
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <>
      {/* Floating Button - positioned above the voice assistant */}
      {!trigger && (
        <div className="fixed bottom-24 right-6 z-40">
          <Button
            size="lg"
            variant="outline"
            className="rounded-full w-12 h-12 shadow-lg bg-white hover:bg-primary/5 border-primary/30"
            onClick={() => setOpen(true)}
            data-testid="premium-translator-btn"
          >
            <Languages className="w-5 h-5 text-primary" />
          </Button>
        </div>
      )}
      
      <Dialog open={open} onOpenChange={setOpen}>
        {trigger && (
          <DialogTrigger asChild>
            {trigger}
          </DialogTrigger>
        )}
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="translator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Premium Translator
            <Badge variant="secondary" className="ml-2">
              <Sparkles className="w-3 h-3 mr-1" />
              AI-Powered
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="text">Text Translation</TabsTrigger>
            <TabsTrigger value="phrases">Medical Phrases</TabsTrigger>
          </TabsList>
          
          <TabsContent value="text" className="space-y-4 mt-4">
            {/* Language Selectors */}
            <div className="flex items-center gap-2">
              <Select value={sourceLang} onValueChange={setSourceLang}>
                <SelectTrigger className="w-full" data-testid="source-lang-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="ghost" size="icon" onClick={swapLanguages} data-testid="swap-langs-btn">
                <ArrowRightLeft className="w-4 h-4" />
              </Button>
              
              <Select value={targetLang} onValueChange={setTargetLang}>
                <SelectTrigger className="w-full" data-testid="target-lang-select">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Source Text */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {LANGUAGES.find(l => l.code === sourceLang)?.flag} Source Text
                </span>
                <div className="flex gap-1">
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-8 w-8"
                    onClick={startVoiceInput}
                    disabled={isListening}
                  >
                    {isListening ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mic className="w-4 h-4" />
                    )}
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-8 w-8"
                    onClick={() => speakText(sourceText, sourceLang)}
                    disabled={!sourceText || isSpeaking}
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Textarea
                placeholder="Enter text to translate..."
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                rows={4}
                data-testid="source-text-input"
              />
            </div>
            
            {/* Translate Button */}
            <Button 
              className="w-full" 
              onClick={handleTranslate}
              disabled={isTranslating || !sourceText.trim()}
              data-testid="translate-btn"
            >
              {isTranslating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Languages className="w-4 h-4 mr-2" />
              )}
              Translate
            </Button>
            
            {/* Translated Text */}
            {translatedText && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {LANGUAGES.find(l => l.code === targetLang)?.flag} Translation
                  </span>
                  <div className="flex gap-1">
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-8 w-8"
                      onClick={() => speakText(translatedText, targetLang)}
                      disabled={isSpeaking}
                    >
                      <Volume2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-8 w-8"
                      onClick={() => copyToClipboard(translatedText)}
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-lg" data-testid="translated-text">{translatedText}</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="phrases" className="space-y-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">
                Quick-translate common medical phrases
              </span>
              <Select value={targetLang} onValueChange={setTargetLang}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.filter(l => l.code !== 'en').map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              {MEDICAL_PHRASES.map((phrase, i) => (
                <Card 
                  key={i} 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleMedicalPhrase(phrase)}
                >
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{phrase.en}</p>
                      <Badge variant="outline" className="text-xs mt-1">{phrase.category}</Badge>
                    </div>
                    <Button size="sm" variant="ghost">
                      <Languages className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Show translation result */}
            {translatedText && activeTab === "phrases" && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge>{LANGUAGES.find(l => l.code === targetLang)?.flag} {LANGUAGES.find(l => l.code === targetLang)?.name}</Badge>
                    <div className="flex gap-1">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8"
                        onClick={() => speakText(translatedText, targetLang)}
                      >
                        <Volume2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(translatedText)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-lg font-medium">{translatedText}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
    </>
  );
}
